// Sign-in through the shared pas-auth Cognito pool: authorization code with PKCE, in the browser,
// no secrets. Each app has one sign-in method (config `auth.identity_provider`): `Entra` sends the
// person straight on to Microsoft (pas-auth federates to the PAS tenant); `COGNITO` shows the
// pool's own sign-in page for its admin-created users.
//
// oidc-client-ts holds the tokens; the REST client asks for one per request and API Gateway's
// JWT authorizer validates it (issuer = the pool, audience = this app's client id) before any
// handler runs. The bearer token is the ACCESS token: it carries `client_id` and the `oid`,
// `name` and `email` claims the pas-auth trigger adds.

import {
  ErrorResponse,
  UserManager,
  WebStorageStateStore,
  type User,
  type UserManagerSettings,
} from 'oidc-client-ts'
import { currentConfig, type RuntimeConfig } from './config'

/** Renew this many seconds before the access token expires (it lives 60 minutes). */
const RENEW_MARGIN_S = 60

/**
 * The library settings for this app. Pure (no globals), so it is unit-tested.
 *
 * - `redirect_uri` / `post_logout_redirect_uri` are the site URL WITH the trailing slash: that is
 *   what pas-auth registers, and Cognito matches callback URLs exactly.
 * - PKCE with S256 is the library default and pas-auth relies on it (its clients are public and
 *   Cognito does not enforce PKCE for them); nothing here turns it off.
 * - Renewal is by refresh token only, and only through `renew()` below. The library's own
 *   `automaticSilentRenew` is OFF: it would call `signinSilent` outside the single-flight guard,
 *   and `signinSilent` without a refresh token falls back to a hidden iframe (the library
 *   defaults `silent_redirect_uri` to `redirect_uri`). `renew()` never calls it without one, so no
 *   iframe is ever created, which is what the CSP's `frame-src 'none'` assumes (and Cognito has
 *   no `prompt=none` for an iframe to use anyway).
 * - Session storage, not local, for the tokens and the sign-in state (PKCE verifier): they die
 *   with the tab, which is the conservative choice for an app that reaches protected health data.
 */
export function userManagerSettings(config: RuntimeConfig, origin: string, storage: Storage): UserManagerSettings {
  const { authority, auth_domain, client_id, identity_provider } = config.auth
  const site = `${origin}/`
  const base = `https://${auth_domain}`
  return {
    authority,
    client_id,
    redirect_uri: site,
    post_logout_redirect_uri: site,
    response_type: 'code',
    scope: 'openid email profile',
    // Skips Cognito's provider chooser: straight to Microsoft (Entra) or to the pool's own sign-in (COGNITO).
    extraQueryParams: { identity_provider },
    // The endpoints are given, not discovered: they live on the pas-auth domain, and nothing is
    // fetched from the issuer's host. `issuer` and `jwks_uri` are informational only.
    // oidc-client-ts 3.x does NOT verify the id_token's signature, `iss`, `aud` or `exp`, so
    // `user.profile` is a display hint and must never be used for anything authoritative (who
    // submitted an order, what someone may do). The API Gateway JWT authorizer, which verifies
    // the access token against the pool's keys, is the authority; the backend reads its claims.
    metadata: {
      issuer: authority,
      authorization_endpoint: `${base}/oauth2/authorize`,
      token_endpoint: `${base}/oauth2/token`,
      userinfo_endpoint: `${base}/oauth2/userInfo`,
      revocation_endpoint: `${base}/oauth2/revoke`,
      jwks_uri: `${authority}/.well-known/jwks.json`,
    },
    userStore: new WebStorageStateStore({ store: storage }),
    stateStore: new WebStorageStateStore({ store: storage }),
    automaticSilentRenew: false,
    accessTokenExpiringNotificationTimeInSeconds: RENEW_MARGIN_S,
    loadUserInfo: false,
  }
}

/**
 * Cognito's sign-out is not OIDC end-session: it is the pool's `/logout` with the client id and
 * the registered sign-out URL (the site URL with its trailing slash), encoded. It ends only the
 * Cognito session; the person stays signed in to Microsoft.
 */
export function logoutUrl(authDomain: string, clientId: string, origin: string): string {
  const q = new URLSearchParams({ client_id: clientId, logout_uri: `${origin}/` })
  return `https://${authDomain}/logout?${q.toString()}`
}

/** The page the pool returned to after sign-in: `?code=` (or `?error=`) with our `state`. */
export function isSigninCallback(search: string): boolean {
  const p = new URLSearchParams(search)
  return p.has('state') && (p.has('code') || p.has('error'))
}

/**
 * Where the callback page goes once the code is exchanged: the same path with the query the
 * sign-in started from (carried through the round trip as `url_state`), so a mid-session sign-in
 * lands back on the same screen. Anything but a query string (or nothing) is dropped.
 */
export function returnUrl(pathname: string, urlState: string | undefined): string {
  const query = typeof urlState === 'string' && urlState.startsWith('?') && !urlState.includes('#') ? urlState : ''
  return `${pathname}${query}`
}

/** What `createAuth` needs from oidc-client-ts' UserManager (a fake in the tests). */
export interface AuthManager {
  getUser(): Promise<User | null>
  signinRedirect(args?: { url_state?: string }): Promise<void>
  signinRedirectCallback(url?: string): Promise<User>
  signinSilent(): Promise<User | null>
  removeUser(): Promise<void>
  revokeTokens(types?: ('access_token' | 'refresh_token')[]): Promise<void>
  events: {
    addUserLoaded(cb: (user: User) => void): unknown
    addUserUnloaded(cb: () => void): unknown
    addAccessTokenExpiring(cb: () => void): unknown
  }
}

/** What `createAuth` needs from the browser (a fake in the tests). */
export interface AuthBrowser {
  origin: string
  search(): string
  pathname(): string
  replaceState(url: string): void
  assign(url: string): void
}

export interface Auth {
  init(): Promise<void>
  activeAccount(): User | null
  signIn(): Promise<void>
  signOut(): Promise<void>
  apiToken(): Promise<string>
}

export function createAuth(manager: AuthManager, browser: AuthBrowser, config: RuntimeConfig): Auth {
  let ready: Promise<void> | null = null
  let current: User | null = null
  let renewing: Promise<User | null> | null = null

  manager.events.addUserLoaded((user) => {
    current = user
  })
  manager.events.addUserUnloaded(() => {
    current = null
  })
  // Renew ahead of expiry, through the same guard as everything else. A failure here is handled
  // where it matters: the next apiToken() renews again or redirects to sign-in.
  manager.events.addAccessTokenExpiring(() => {
    renew().catch(() => {})
  })

  /**
   * Renews by refresh token, once at a time (refresh tokens rotate; parallel callers share one
   * renewal). Null when there is no refresh token, or when the pool refuses it (an OAuth error
   * such as `invalid_grant`): the session is over, the user is forgotten, and the caller sends the
   * person to sign in. A network error or timeout is rethrown and the user kept: the session may
   * still be good once the network is back.
   */
  function renew(): Promise<User | null> {
    renewing ??= (async () => {
      try {
        const user = await manager.getUser()
        // Never signinSilent without a refresh token: that path is the iframe.
        if (!user?.refresh_token) return null
        return await manager.signinSilent()
      } catch (e) {
        if (e instanceof ErrorResponse) {
          await manager.removeUser().catch(() => {})
          return null
        }
        throw e
      } finally {
        renewing = null
      }
    })()
    return renewing
  }

  function redirectToSignIn(): Promise<void> {
    const search = browser.search()
    return manager.signinRedirect(search ? { url_state: search } : undefined)
  }

  async function start(): Promise<void> {
    if (isSigninCallback(browser.search())) {
      let user: User | null = null
      try {
        user = await manager.signinRedirectCallback()
        current = user
      } finally {
        // The code is single-use: drop it from the address either way, so a reload starts clean,
        // and put back the query the sign-in started from.
        browser.replaceState(returnUrl(browser.pathname(), user?.url_state))
      }
    } else {
      current = await manager.getUser()
    }
    if (current?.expired) current = await renew()
  }

  function init(): Promise<void> {
    ready ??= start().catch((e: unknown) => {
      ready = null
      throw e
    })
    return ready
  }

  return {
    init,
    activeAccount: () => current,
    async signIn() {
      await init()
      await redirectToSignIn()
    },
    async signOut() {
      await init()
      // Revoke the refresh token so it cannot outlive the sign-out; best effort.
      await manager.revokeTokens(['refresh_token']).catch(() => {})
      await manager.removeUser()
      browser.assign(logoutUrl(config.auth.auth_domain, config.auth.client_id, browser.origin))
    },
    async apiToken() {
      await init()
      let user = current ?? (await manager.getUser())
      const left = user?.expires_in
      if (user && left !== undefined && left < RENEW_MARGIN_S) user = await renew()
      if (!user) {
        await redirectToSignIn()
        throw new Error('Sign-in required')
      }
      return user.access_token
    },
  }
}

let auth: Auth | null = null

function browserAuth(): Auth {
  if (auth) return auth
  const config = currentConfig()
  const manager = new UserManager(userManagerSettings(config, window.location.origin, window.sessionStorage))
  auth = createAuth(
    manager,
    {
      origin: window.location.origin,
      search: () => window.location.search,
      pathname: () => window.location.pathname,
      replaceState: (url) => window.history.replaceState(null, '', url),
      assign: (url) => window.location.assign(url),
    },
    config,
  )
  return auth
}

/** Creates the sign-in client and completes any redirect already in flight. */
export function initAuth(): Promise<void> {
  try {
    return browserAuth().init()
  } catch (e) {
    return Promise.reject(e instanceof Error ? e : new Error(String(e)))
  }
}

/** The signed-in user, or null (bootstrap then signs in). */
export function activeAccount(): User | null {
  return auth?.activeAccount() ?? null
}

/**
 * Sends the user to pas-auth (and on to Microsoft), carrying the current query so the same
 * screen comes back. Resolves only by navigating away.
 */
export async function signIn(): Promise<void> {
  await browserAuth().signIn()
}

/**
 * Revokes the refresh token, forgets the local tokens, then the pool's logout endpoint, which
 * returns to the site. Resolves only by navigating away.
 */
export async function signOut(): Promise<void> {
  await browserAuth().signOut()
}

/**
 * The bearer token for the Glasses Orders API: the access token, renewed by refresh token
 * when it is about to expire. A session that cannot be renewed redirects to sign-in (the order
 * screen keeps the draft before it submits; the query comes back as `url_state`) and this rejects.
 */
export async function apiToken(): Promise<string> {
  return browserAuth().apiToken()
}
